import { HTTP_INTERCEPTORS } from '@angular/common/http';
import { APP_INITIALIZER, ApplicationConfig, importProvidersFrom, LOCALE_ID } from '@angular/core';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter, withInMemoryScrolling, withRouterConfig } from '@angular/router';
import {
  MMU_APP_CONTEXT,
  MMU_MENU_ITEMS,
  MmuAppContext,
  MmuFeatureFlagsService,
  MmuModule,
  MmuUserService
} from '@move/core';
import { MuiI18nProvider, MuiModule, MUI_CONFIGURATION } from '@move/ui';
import { EffectsModule, provideEffects } from '@ngrx/effects';
import { provideStore, StoreModule } from '@ngrx/store';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { TranslateLoader, TranslateModule, TranslateService } from '@ngx-translate/core';
import { AppTranslateLoader } from './core/i18n/app-translate-loader';
import { DynamicLocaleId } from './core/i18n/dynamic-locale-id';
import { localeInitializer } from './core/i18n/locale-initializer';
import { MuiI18nProviderImpl } from './core/i18n/mui-i18n-provider-impl';
import { ApiErrorInterceptor } from './core/interceptors/api-error.interceptor';
import { SerializerInterceptor } from './core/interceptors/serializer.interceptor';
import { mmuItemsFactory } from './core/menus/mmu-menu-items-factory';
import { MuiConfigurationImpl } from './core/mui/mui-configuration-impl';
import { ROUTES } from './core/routing/routes';


export const APP_CONFIG: ApplicationConfig = {
  providers: [
    provideRouter(
      ROUTES,
      withRouterConfig({
        paramsInheritanceStrategy: 'always'
      }),
      withInMemoryScrolling({
        anchorScrolling: 'enabled',
        scrollPositionRestoration: 'disabled'
      })
    ),
    provideAnimations(),
    provideStore(),
    provideEffects(),
    provideStoreDevtools({ maxAge: 25, logOnly: !localStorage.getItem('StoreDevtoolsModule') }),
    importProvidersFrom(TranslateModule.forRoot({
      defaultLanguage: 'en-US',
      useDefaultLang: true,
      loader: { provide: TranslateLoader, useClass: AppTranslateLoader },
      // isolate: false and extend: true are required to make the merge of translations with the root TranslateService work
      // See: https://github.com/ngx-translate/core/issues/1193#issuecomment-837340141
      isolate: false,
      extend: true
    })),
    // We need this for backward compatibility with Angular 15 Verticals,
    // provideStore() and provideEffects() doesn't work
    // remove after upgrading
    importProvidersFrom(
      StoreModule.forRoot({}),
      EffectsModule.forRoot([])
    ),
    importProvidersFrom(MuiModule.forRoot()),
    importProvidersFrom(MmuModule.forRoot({
      userInfoEndpointUrl: '/user/api/userInfo'
    })),
    { provide: MMU_APP_CONTEXT, useValue: MmuAppContext.CONTAINER },
    {
      provide: MMU_MENU_ITEMS, useFactory: mmuItemsFactory, deps: [
        MmuUserService,
        MMU_APP_CONTEXT,
        MmuFeatureFlagsService
      ]
    },
    { provide: APP_INITIALIZER, useFactory: localeInitializer, deps: [LOCALE_ID, TranslateService], multi: true },
    { provide: LOCALE_ID, useClass: DynamicLocaleId, deps: [TranslateService] },
    { provide: MuiI18nProvider, useClass: MuiI18nProviderImpl, deps: [TranslateService] },
    { provide: MUI_CONFIGURATION, useClass: MuiConfigurationImpl, deps: [MmuUserService] },
    { provide: HTTP_INTERCEPTORS, useClass: ApiErrorInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: SerializerInterceptor, multi: true }
  ]
};
